import React from "react";
import Modal from "react-modal";
import { errorReporter } from "util/errorReporter";
import { fetchWrapper } from "util/api";

import "./PasswordModal.scss";
import { Meet } from "types";
import get from "lodash/get";
import PastDueInvoicesModal from "components/pastDueInvoicesModal/PastDueInvoicesModal";

type PasswordModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onRequestClose: () => void;
  meet: Meet;
  meetId: string;
  onOk: (password: string) => Promise<void> | null;
};
type PasswordModalState = {
  password: string;
  passwordConfirmation: string;
  error: string;
  step:
    | "initial"
    | "setPassword"
    | "confirmNewToken"
    | "confirmOldToken"
    | "forgotPassword"
    | "resetPassword"
    | "resetComplete";
  confirmationToken: string;
  loading: boolean;
};

const initialState: PasswordModalState = {
  password: "",
  passwordConfirmation: "",
  error: "",
  step: "initial",
  confirmationToken: "",
  loading: false,
};

const PasswordModal = ({
  meet,
  meetId,
  onCancel,
  onRequestClose,
  isOpen,
  onOk,
}: PasswordModalProps) => {
  const [pastDueInvoicesModalOpen, setPastDueInvoicesModalOpen] =
    React.useState<false | { name: string; invoiceUrl: string }[]>(false);
  const [state, setState] = React.useState<PasswordModalState>(initialState);

  const onChange = (
    value: string,
    name: "password" | "passwordConfirmation" | "confirmationToken"
  ) => {
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const onError = (errorMessage: string) => {
    onCancel();
    onCloseAndResetForm();
    alert(errorMessage);
  };

  const onCancelInternal = () => {
    onCancel();
    onCloseAndResetForm();
  };

  const onCloseAndResetForm = () => {
    setState(initialState);
    onRequestClose();
  };

  const onClickForgotPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    setState((prev) => ({ ...prev, step: "forgotPassword" }));
  };

  const onClickGoToReset = (event: React.MouseEvent<HTMLInputElement>) => {
    event.preventDefault();
    setState((prev) => ({ ...prev, step: "resetPassword" }));
  };

  const onClickSendEmailTokenForReset = async (
    event: React.MouseEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const response = await fetchWrapper(
        `/api/meets/${meetId}/send_reset_token`,
        "POST"
      );
      if (response.ok) {
        setState((prev) => ({
          ...prev,
          step: "resetPassword",
          loading: false,
        }));
      } else {
        if (response.invoices) {
          setPastDueInvoicesModalOpen(response.invoices);
          setState((prev) => ({ ...prev, loading: false }));
        } else {
          onError(response.error);
        }
      }
    } catch (error: any) {
      errorReporter({ message: "Send Reset Token Failure", error });
      onError("Something went wrong sending password reset token");
    }
  };

  const onClickResetPassword = async (
    event: React.MouseEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const response = await fetchWrapper(
        `/api/meets/${meetId}/reset_password`,
        "POST",
        {
          confirmation_token: state.confirmationToken,
          password: state.password,
        }
      );
      if (response.ok) {
        setState((prev) => ({
          ...prev,
          step: "resetComplete",
          loading: false,
        }));
      } else {
        if (response.invoices) {
          setPastDueInvoicesModalOpen(response.invoices);
          setState((prev) => ({ ...prev, loading: false }));
        } else {
          onError(response.error);
        }
      }
    } catch (error: any) {
      errorReporter({ message: "Password Reset Failure", error });
      onError("Something went wrong resetting password");
    }
  };

  const onClickContinue = async (event: React.MouseEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (!state.password) {
      setState((prev) => ({ ...prev, error: "Must enter a password." }));
      return;
    }

    setState((prev) => ({ ...prev, loading: true }));
    await onOk(state.password);
    onCloseAndResetForm();
  };

  const validated = get(meet, "validated");

  return (
    <>
      <Modal
        ariaHideApp={false}
        isOpen={isOpen}
        onRequestClose={onCancelInternal}
        shouldCloseOnOverlayClick={false}
        className={{
          base: "password-modal",
          afterOpen: "password-modal-after-open",
          beforeClose: "password-modal-before-close",
        }}
        overlayClassName={{
          base: "modal-overlay",
          afterOpen: "",
          beforeClose: "",
        }}
        contentLabel="password modal"
      >
        <div className="content">
          {state.loading && <div className="login-loading-overlay"></div>}
          {state.loading && (
            <div className="login-loading-text">Loading...</div>
          )}

          <div>
            {state.step === "resetComplete" && (
              <div style={{ marginBottom: 20 }}>Password Reset Complete</div>
            )}
            <div className="title">Meet Director Login</div>
            {(state.step === "initial" || state.step === "resetComplete") && (
              <div>
                <form>
                  <input
                    type="text"
                    name="username"
                    defaultValue={meetId}
                    style={{ display: "none" }}
                  />
                  <input
                    autoFocus
                    type="password"
                    placeholder="password"
                    name="current-password"
                    onChange={(e) => onChange(e.target.value, "password")}
                  />
                  <div className="error">
                    {state.error && <div>{state.error}</div>}
                  </div>
                  <div className="button-row">
                    <input
                      disabled={state.loading}
                      className="password-cancel-button"
                      type="button"
                      onClick={onCancelInternal}
                      value="Cancel"
                    />
                    <input
                      disabled={state.loading}
                      type="submit"
                      onClick={onClickContinue}
                      value="Continue"
                    />
                  </div>
                </form>
                {validated && (
                  <div className="forgot-password-button">
                    <button
                      disabled={state.loading}
                      onClick={onClickForgotPassword}
                    >
                      I am a meet director and I forgot my password
                    </button>
                  </div>
                )}
                <div style={{ marginTop: 10 }}>
                  This login is for meet staff only. There is no login for
                  lifters. If you are a lifter and need to make changes to your
                  registration contact the meet director.
                </div>
              </div>
            )}
            {state.step === "forgotPassword" && (
              <div>
                <div style={{ marginBottom: 20 }}>
                  Do you have the validation token that was sent when you
                  confirmed your email address?
                </div>
                <div className="button-row">
                  <input
                    className="password-cancel-button"
                    type="button"
                    onClick={onCancelInternal}
                    value="Cancel"
                  />
                  <div>
                    <input
                      disabled={state.loading}
                      style={{ marginBottom: 20, minWidth: 200 }}
                      type="button"
                      onClick={onClickGoToReset}
                      value="I Already Have A Token"
                    />
                    <input
                      disabled={state.loading}
                      style={{ minWidth: 200 }}
                      type="submit"
                      onClick={onClickSendEmailTokenForReset}
                      value="Send New Confirmation Token"
                    />
                  </div>
                </div>
              </div>
            )}
            {state.step === "resetPassword" && (
              <form>
                <input
                  type="text"
                  name="username"
                  defaultValue={meetId}
                  style={{ display: "none" }}
                />
                <div>
                  Enter confirmation token sent to you when you validated your
                  email.
                </div>
                <input
                  autoFocus
                  type="text"
                  placeholder="confirmation token"
                  name="confirmation-token"
                  onChange={(e) =>
                    onChange(e.target.value, "confirmationToken")
                  }
                  style={{ marginBottom: 20, marginTop: 20 }}
                />
                <input
                  type="password"
                  placeholder="new password"
                  name="current-password"
                  onChange={(e) => onChange(e.target.value, "password")}
                />
                <input
                  type="password"
                  className="confirm-password"
                  placeholder="confirm new password"
                  name="confirm-password"
                  onChange={(e) =>
                    onChange(e.target.value, "passwordConfirmation")
                  }
                />
                <div className="error">
                  {state.error && <div>{state.error}</div>}
                </div>
                <div className="button-row">
                  <input
                    disabled={state.loading}
                    className="password-cancel-button"
                    type="button"
                    onClick={onCancelInternal}
                    value="Cancel"
                  />
                  <input
                    disabled={state.loading}
                    type="submit"
                    onClick={onClickResetPassword}
                    value="Reset Password"
                  />
                </div>
              </form>
            )}
          </div>
        </div>
      </Modal>
      <PastDueInvoicesModal
        isOpen={!!pastDueInvoicesModalOpen}
        onRequestClose={() => setPastDueInvoicesModalOpen(false)}
        invoices={pastDueInvoicesModalOpen || []}
      />
    </>
  );
};

export default PasswordModal;
